import React, { useState } from 'react';
import {
  Search,
  Loader2,
  Users,
  CheckCircle2,
  XCircle,
  HelpCircle,
  ExternalLink,
  AlertCircle,
} from 'lucide-react';

type CheckStatus = 'found' | 'absent' | 'unknown' | 'timeout' | 'error';

interface CheckResult {
  name: string;
  category: string;
  url: string;
  status: CheckStatus;
}

const API = '/api/socmint';

async function callApi<T>(username: string): Promise<T> {
  const res = await fetch(`${API}?q=${encodeURIComponent(username)}`);
  const raw = await res.text();
  let data: any;
  try {
    data = JSON.parse(raw);
  } catch {
    throw new Error(
      "The lookup service isn't responding. Locally, use `vercel dev` — the plain dev server " +
        "doesn't run the /api function."
    );
  }
  if (!res.ok) throw new Error(data?.error || `Request failed (${res.status}).`);
  return data as T;
}

// Platforms we can't reliably auto-check (they block bots / 200 for everything).
// Built as clickable pivot links instead. {u} = username, {n} = full name.
const PIVOTS: { name: string; category: string; url: (u: string, n: string) => string }[] = [
  { name: 'X / Twitter', category: 'Social', url: (u) => `https://x.com/${u}` },
  { name: 'Instagram', category: 'Social', url: (u) => `https://instagram.com/${u}` },
  { name: 'TikTok', category: 'Social', url: (u) => `https://tiktok.com/@${u}` },
  { name: 'Facebook', category: 'Social', url: (u) => `https://facebook.com/${u}` },
  { name: 'YouTube', category: 'Social', url: (u) => `https://youtube.com/@${u}` },
  { name: 'Threads', category: 'Social', url: (u) => `https://threads.net/@${u}` },
  { name: 'Snapchat', category: 'Social', url: (u) => `https://snapchat.com/add/${u}` },
  { name: 'Twitch', category: 'Gaming', url: (u) => `https://twitch.tv/${u}` },
  {
    name: 'LinkedIn (people search)',
    category: 'Professional',
    url: (u, n) =>
      `https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(n || u)}`,
  },
];

// One-click enumeration / search helpers seeded with the handle.
const TOOLS: { name: string; url: (u: string, n: string) => string }[] = [
  { name: 'WhatsMyName', url: () => 'https://whatsmyname.app/' },
  { name: 'Namechk', url: (u) => `https://namechk.com/` },
  { name: 'Google "handle"', url: (u) => `https://www.google.com/search?q=%22${encodeURIComponent(u)}%22` },
  {
    name: 'Google name + keywords',
    url: (u, n) => `https://www.google.com/search?q=${encodeURIComponent(`"${n || u}"`)}`,
  },
];

const statusMeta: Record<CheckStatus, { label: string; icon: React.ReactNode; cls: string }> = {
  found: {
    label: 'Found',
    icon: <CheckCircle2 className="w-4 h-4" />,
    cls: 'text-green-600 dark:text-green-400',
  },
  absent: {
    label: 'Not found',
    icon: <XCircle className="w-4 h-4" />,
    cls: 'text-gray-400 dark:text-slate-500',
  },
  unknown: {
    label: 'Unknown',
    icon: <HelpCircle className="w-4 h-4" />,
    cls: 'text-amber-500',
  },
  timeout: { label: 'Timed out', icon: <HelpCircle className="w-4 h-4" />, cls: 'text-amber-500' },
  error: { label: 'Error', icon: <HelpCircle className="w-4 h-4" />, cls: 'text-amber-500' },
};

const SocmintLookup: React.FC = () => {
  const [username, setUsername] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<CheckResult[] | null>(null);

  const run = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const u = username.trim();
    if (!u) return;
    setError(null);
    setResults(null);
    setLoading(true);
    try {
      const data = await callApi<{ results: CheckResult[] }>(u);
      // found first, then unknown, then absent — most useful at the top
      const order: Record<CheckStatus, number> = { found: 0, unknown: 1, timeout: 1, error: 1, absent: 2 };
      setResults([...data.results].sort((a, b) => order[a.status] - order[b.status]));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  const u = encodeURIComponent(username.trim());
  const foundCount = results?.filter((r) => r.status === 'found').length ?? 0;

  return (
    <div className="mt-4 rounded-lg border border-orange-200 dark:border-orange-500/30 bg-orange-50/60 dark:bg-orange-500/5 p-4">
      <div className="flex items-center gap-2 mb-1">
        <Users className="w-4 h-4 text-orange-500" />
        <h4 className="font-bold text-slate-900 dark:text-slate-100">Live username enumeration</h4>
      </div>
      <p className="text-xs text-gray-500 dark:text-slate-400 mb-3">
        Auto-checks platforms that reliably confirm an account exists, and builds pivot links for the
        rest. Reused handles are strong leads — always verify a hit is the same person.
      </p>

      <form onSubmit={run} className="flex flex-col sm:flex-row gap-2 mb-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Username / handle (e.g. janedoe)"
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>
        <input
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          placeholder="Full name (optional)"
          className="sm:w-52 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
        />
        <button
          type="submit"
          disabled={loading || !username.trim()}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50 transition-colors"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          Run
        </button>
      </form>

      {error && (
        <div className="flex items-start gap-2 text-sm text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-lg p-3 mb-3">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* auto-check results */}
      {results && (
        <div className="mb-4">
          <p className="text-xs font-bold tracking-wide uppercase text-orange-500 mb-2">
            Auto-checked ({foundCount} found)
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {results.map((r) => {
              const m = statusMeta[r.status];
              return (
                <a
                  key={r.name}
                  href={r.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg border bg-white dark:bg-slate-800 transition-colors hover:border-orange-300 ${
                    r.status === 'found'
                      ? 'border-green-200 dark:border-green-500/30'
                      : 'border-gray-200 dark:border-slate-700'
                  }`}
                >
                  <span className="flex items-center gap-2 text-sm">
                    <span className={m.cls}>{m.icon}</span>
                    <span className="font-semibold text-slate-900 dark:text-slate-100">{r.name}</span>
                    <span className="text-xs text-gray-400">{r.category}</span>
                  </span>
                  <span className={`text-xs font-medium ${m.cls}`}>{m.label}</span>
                </a>
              );
            })}
          </div>
        </div>
      )}

      {/* pivot links — only shown once a handle is entered */}
      {username.trim() && (
        <>
          <p className="text-xs font-bold tracking-wide uppercase text-orange-500 mb-2">
            Manual pivots (can't be auto-verified)
          </p>
          <div className="flex flex-wrap gap-1.5 mb-4">
            {PIVOTS.map((p) => (
              <a
                key={p.name}
                href={p.url(u, fullName.trim())}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-lg bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-orange-300 hover:text-orange-600 transition-colors"
              >
                {p.name}
                <ExternalLink className="w-3 h-3" />
              </a>
            ))}
          </div>

          <p className="text-xs font-bold tracking-wide uppercase text-orange-500 mb-2">
            Enumeration & search tools
          </p>
          <div className="flex flex-wrap gap-1.5">
            {TOOLS.map((t) => (
              <a
                key={t.name}
                href={t.url(u, fullName.trim())}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-lg bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-orange-300 hover:text-orange-600 transition-colors"
              >
                {t.name}
                <ExternalLink className="w-3 h-3" />
              </a>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default SocmintLookup;
